// ═══════════════════════════════════════════════════════════════════════════════
// DEAL INTELLIGENCE DASHBOARD v1.0
// EnergiPro Solutions — Client Activity & Deal Health Visibility
// ═══════════════════════════════════════════════════════════════════════════════
// Features: Deal Health Score, Temperature Badges, Activity Timeline,
//           Automated Actions, Intervention System, Signals Panel
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  Activity, TrendingUp, AlertCircle, Phone, Mail, ArrowUpCircle,
  Eye, Clock, RefreshCw, Shield, ChevronDown, ChevronUp,
  Flame, Snowflake, Sun, Target, Zap, Send, UserCheck,
  MessageSquare, XCircle, CheckCircle2, BarChart3, Bell
} from 'lucide-react';
import {
  DealIntelligenceRecord, DealTemperature, InterventionType,
  ActivityType, TEMPERATURE_STYLES, ACTIVITY_LABELS,
  generateMockDealIntelligence, recordIntervention,
} from '../../components/rfq/dealIntelligenceEngine';

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export const DealIntelligence: React.FC = () => {
  const [deals, setDeals] = useState<DealIntelligenceRecord[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterTemp, setFilterTemp] = useState<string>('all');
  const [notification, setNotification] = useState<string | null>(null);
  const [interventionModal, setInterventionModal] = useState<{ rfqId: string; type: InterventionType } | null>(null);
  const [interventionNotes, setInterventionNotes] = useState('');
  const [showAlerts, setShowAlerts] = useState(true);

  useEffect(() => {
    setDeals(generateMockDealIntelligence());
  }, []);

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 4000);
  };

  // ─── INTERVENTION HANDLER ────────────────────────────────────────────────

  const handleIntervention = (rfqId: string, type: InterventionType) => {
    setInterventionModal({ rfqId, type });
    setInterventionNotes('');
  };

  const confirmIntervention = () => {
    if (!interventionModal) return;
    const { rfqId, type } = interventionModal;
    setDeals(prev => prev.map(d => {
      if (d.rfqId !== rfqId) return d;
      return recordIntervention(d, type, 'Admin', interventionNotes || undefined);
    }));
    const labels: Record<InterventionType, string> = {
      call_client: 'Call scheduled',
      send_custom_email: 'Custom email sent',
      escalate: 'Escalated to management',
    };
    showNotification(`✓ ${labels[type]} for ${interventionModal.rfqId}`);
    setInterventionModal(null);
  };

  // ─── FILTER & SORT ───────────────────────────────────────────────────────

  const filteredDeals = deals
    .filter(d => filterTemp === 'all' || d.temperature === filterTemp)
    .sort((a, b) => {
      // Hot first, then by score descending
      const tempOrder: Record<DealTemperature, number> = { hot: 0, warm: 1, cold: 2 };
      const tempDiff = tempOrder[a.temperature] - tempOrder[b.temperature];
      if (tempDiff !== 0) return tempDiff;
      return b.dealHealthScore - a.dealHealthScore;
    });

  // ─── KPIs ─────────────────────────────────────────────────────────────────

  const hotDeals = deals.filter(d => d.temperature === 'hot').length;
  const warmDeals = deals.filter(d => d.temperature === 'warm').length;
  const coldDeals = deals.filter(d => d.temperature === 'cold').length;
  const avgHealth = deals.length > 0 ? Math.round(deals.reduce((s, d) => s + d.dealHealthScore, 0) / deals.length) : 0;
  const atRisk = deals.filter(d => d.temperature === 'cold' && d.daysWithoutResponse >= 7).length;

  // ─── HELPER RENDERS ────────────────────────────────────────────────────────

  const formatDate = (isoDate: string | null): string => {
    if (!isoDate) return '—';
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTemperatureIcon = (temp: DealTemperature) => {
    switch (temp) {
      case 'hot': return <Flame className="w-4 h-4 text-red-400" />;
      case 'warm': return <Sun className="w-4 h-4 text-amber-400" />;
      case 'cold': return <Snowflake className="w-4 h-4 text-blue-400" />;
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-red-400';
    if (score >= 50) return 'text-amber-400';
    return 'text-blue-400';
  };

  const getScoreBarColor = (score: number): string => {
    if (score >= 80) return 'bg-gradient-to-r from-red-500 to-orange-500';
    if (score >= 50) return 'bg-gradient-to-r from-amber-500 to-yellow-500';
    return 'bg-gradient-to-r from-blue-500 to-cyan-500';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide flex items-center gap-3">
                <Activity className="w-8 h-8 text-orange-400" />
                Deal Intelligence
              </h1>
              <p className="text-slate-400 text-sm mt-1">Real-time deal health monitoring & intervention system</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDeals(generateMockDealIntelligence())}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:bg-slate-700 transition-all"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Intelligence</span>
              </div>
            </div>
          </div>

          {/* ═══ HOT DEAL ALERT BANNER ═══ */}
          {showAlerts && hotDeals > 0 && (
            <div className="mb-6 bg-gradient-to-r from-red-900/30 to-orange-900/20 border border-red-500/40 rounded-xl p-4 animate-pulse-slow relative">
              <button
                onClick={() => setShowAlerts(false)}
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-300"
              >
                <XCircle className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Bell className="w-5 h-5 text-red-400 animate-bounce" />
                </div>
                <div>
                  <h3 className="text-red-300 font-bold text-sm uppercase tracking-wider">
                    🔥 {hotDeals} High-Probability Deal{hotDeals > 1 ? 's' : ''} Detected
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">
                    Strong buying signals identified — immediate action recommended for maximum conversion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ═══ COLD DEAL AT-RISK BANNER ═══ */}
          {atRisk > 0 && (
            <div className="mb-6 bg-gradient-to-r from-blue-900/20 to-slate-900/20 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-blue-300 font-bold text-sm uppercase tracking-wider">
                    ❄️ {atRisk} Deal{atRisk > 1 ? 's' : ''} At Risk
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">
                    Extended inactivity detected. Re-engagement emails queued. Manual intervention recommended.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ═══ KPI STRIP ═══ */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{deals.length}</div>
              <div className="text-slate-400 text-xs mt-1">Active Deals</div>
            </div>
            <div className="bg-[#111827] border border-red-500/30 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-red-400">{hotDeals}</div>
              <div className="text-slate-400 text-xs mt-1">🔥 Hot</div>
            </div>
            <div className="bg-[#111827] border border-amber-500/30 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-amber-400">{warmDeals}</div>
              <div className="text-slate-400 text-xs mt-1">☀️ Warm</div>
            </div>
            <div className="bg-[#111827] border border-blue-500/30 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{coldDeals}</div>
              <div className="text-slate-400 text-xs mt-1">❄️ Cold</div>
            </div>
            <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-4 text-center">
              <div className={`text-2xl font-bold ${getScoreColor(avgHealth)}`}>{avgHealth}</div>
              <div className="text-slate-400 text-xs mt-1">Avg Health</div>
            </div>
          </div>

          {/* ═══ FILTER BAR ═══ */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-slate-400 text-sm">Filter:</span>
            {['all', 'hot', 'warm', 'cold'].map(temp => {
              const isActive = filterTemp === temp;
              const styles = temp === 'all'
                ? { bg: 'bg-slate-700/50', text: 'text-slate-300', border: 'border-slate-600' }
                : TEMPERATURE_STYLES[temp as DealTemperature];
              return (
                <button
                  key={temp}
                  onClick={() => setFilterTemp(temp)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    isActive
                      ? `${styles.bg} ${styles.text} ${styles.border} ring-1 ring-offset-0`
                      : 'bg-slate-800/50 text-slate-500 border-slate-700 hover:text-slate-300'
                  }`}
                >
                  {temp === 'all' ? 'All Deals' : `${TEMPERATURE_STYLES[temp as DealTemperature].icon} ${temp.charAt(0).toUpperCase() + temp.slice(1)}`}
                </button>
              );
            })}
          </div>

          {/* ═══ DEAL CARDS ═══ */}
          <div className="space-y-4">
            {filteredDeals.map(deal => {
              const isExpanded = expandedId === deal.rfqId;
              const tempStyle = TEMPERATURE_STYLES[deal.temperature];

              return (
                <div
                  key={deal.rfqId}
                  className={`bg-[#111827] border rounded-xl overflow-hidden transition-all ${
                    deal.temperature === 'hot'
                      ? 'border-red-500/40 ring-1 ring-red-500/20'
                      : deal.temperature === 'cold'
                      ? 'border-blue-500/30'
                      : 'border-slate-700/50'
                  }`}
                >
                  {/* Card Header */}
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-800/30 transition-all"
                    onClick={() => setExpandedId(isExpanded ? null : deal.rfqId)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Temperature Badge */}
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${tempStyle.bg} ${tempStyle.border}`}>
                          {getTemperatureIcon(deal.temperature)}
                          <span className={`text-xs font-bold ${tempStyle.text}`}>{tempStyle.label}</span>
                        </div>

                        {/* Client Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold text-sm">{deal.clientName}</span>
                            <span className="text-slate-500 text-xs">({deal.companyName})</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-slate-400 text-xs">{deal.referenceId}</span>
                            <span className="text-slate-600">•</span>
                            <span className="text-slate-400 text-xs">{deal.serviceName}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Health Score */}
                        <div className="text-right">
                          <div className={`text-xl font-bold ${getScoreColor(deal.dealHealthScore)}`}>
                            {deal.dealHealthScore}
                          </div>
                          <div className="text-slate-500 text-[10px] uppercase">Health</div>
                        </div>

                        {/* Score Bar */}
                        <div className="w-24 hidden md:block">
                          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${getScoreBarColor(deal.dealHealthScore)}`}
                              style={{ width: `${deal.dealHealthScore}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-slate-600 text-[9px]">0</span>
                            <span className="text-slate-600 text-[9px]">100</span>
                          </div>
                        </div>

                        {/* Last Activity */}
                        <div className="text-right hidden lg:block">
                          <div className="text-slate-300 text-xs">{formatDate(deal.lastActivityDate)}</div>
                          <div className="text-slate-500 text-[10px]">Last activity</div>
                        </div>

                        {/* Expand */}
                        {isExpanded
                          ? <ChevronUp className="w-5 h-5 text-slate-500" />
                          : <ChevronDown className="w-5 h-5 text-slate-500" />
                        }
                      </div>
                    </div>

                    {/* Quick Info Row */}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <BarChart3 className="w-3 h-3" />
                        <span>RFQ Score: <span className="text-slate-300">{deal.rfqScore}</span></span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Activity className="w-3 h-3" />
                        <span>Freq: <span className="text-slate-300">{deal.activityFrequency}/wk</span></span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>Silent: <span className={deal.daysWithoutResponse >= 5 ? 'text-red-400' : 'text-slate-300'}>{deal.daysWithoutResponse}d</span></span>
                      </div>
                      {deal.lastActivityType && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <span>{ACTIVITY_LABELS[deal.lastActivityType].icon}</span>
                          <span className="text-slate-300">{ACTIVITY_LABELS[deal.lastActivityType].label}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="border-t border-slate-700/50 p-5 bg-slate-900/30">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Column 1: Signals & Breakdown */}
                        <div>
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Target className="w-3.5 h-3.5 text-orange-400" /> Deal Signals
                          </h4>
                          <div className="space-y-2">
                            {deal.signals.map((signal, idx) => (
                              <div
                                key={idx}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                                  signal.type === 'positive'
                                    ? 'bg-green-500/5 border-green-500/20'
                                    : signal.type === 'negative'
                                    ? 'bg-red-500/5 border-red-500/20'
                                    : 'bg-slate-500/5 border-slate-500/20'
                                }`}
                              >
                                <span className="text-xs text-slate-300">{signal.label}</span>
                                <span className={`text-xs font-bold ${
                                  signal.impact > 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {signal.impact > 0 ? '+' : ''}{signal.impact}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Score Breakdown */}
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mt-4 mb-3">Score Breakdown</h4>
                          <div className="text-xs text-slate-400 space-y-1.5">
                            <div className="flex justify-between"><span>Recommendation:</span></div>
                            <div className="text-slate-300 bg-slate-800/50 rounded-lg p-2 text-xs mt-1">
                              {deal.recommendation}
                            </div>
                          </div>
                        </div>

                        {/* Column 2: Activity Timeline */}
                        <div>
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Eye className="w-3.5 h-3.5 text-cyan-400" /> Activity Timeline
                          </h4>
                          <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                            {deal.activities
                              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                              .map((act, idx) => (
                                <div key={idx} className="flex items-start gap-2 px-3 py-2 bg-slate-800/40 rounded-lg border border-slate-700/30">
                                  <span className="text-sm mt-0.5">{ACTIVITY_LABELS[act.type].icon}</span>
                                  <div className="flex-1">
                                    <div className="text-xs text-slate-300 font-medium">{ACTIVITY_LABELS[act.type].label}</div>
                                    {act.details && <div className="text-[10px] text-slate-500 mt-0.5">{act.details}</div>}
                                    <div className="text-[10px] text-slate-600 mt-0.5">{formatDate(act.timestamp)}</div>
                                  </div>
                                  <span className="text-[9px] text-slate-600 uppercase">{act.source}</span>
                                </div>
                              ))}
                          </div>
                        </div>

                        {/* Column 3: Actions & Interventions */}
                        <div>
                          {/* Automated Actions */}
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5 text-yellow-400" /> Automated Actions
                          </h4>
                          <div className="space-y-2 mb-4">
                            {deal.automatedActions.length > 0 ? deal.automatedActions.map((action, idx) => (
                              <div
                                key={idx}
                                className={`px-3 py-2 rounded-lg border ${
                                  action.urgency === 'immediate'
                                    ? 'bg-red-500/5 border-red-500/20'
                                    : 'bg-amber-500/5 border-amber-500/20'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    action.urgency === 'immediate' ? 'bg-red-400 animate-pulse' : 'bg-amber-400'
                                  }`} />
                                  <span className="text-xs text-slate-300 font-medium">{action.label}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-1 ml-3.5">{action.description}</p>
                              </div>
                            )) : (
                              <div className="text-xs text-slate-600 italic">No automated actions triggered</div>
                            )}
                          </div>

                          {/* Intervention Buttons */}
                          <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <UserCheck className="w-3.5 h-3.5 text-green-400" /> Admin Interventions
                          </h4>
                          <div className="grid grid-cols-1 gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleIntervention(deal.rfqId, 'call_client'); }}
                              className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-xs font-medium hover:bg-green-500/20 transition-all"
                            >
                              <Phone className="w-3.5 h-3.5" /> Call Client
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleIntervention(deal.rfqId, 'send_custom_email'); }}
                              className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-300 text-xs font-medium hover:bg-cyan-500/20 transition-all"
                            >
                              <Mail className="w-3.5 h-3.5" /> Send Custom Email
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleIntervention(deal.rfqId, 'escalate'); }}
                              className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs font-medium hover:bg-amber-500/20 transition-all"
                            >
                              <ArrowUpCircle className="w-3.5 h-3.5" /> Escalate
                            </button>
                          </div>

                          {/* Intervention History */}
                          {deal.interventions.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-slate-500 text-[10px] uppercase tracking-wider mb-2">Intervention Log</h5>
                              {deal.interventions.map((int, idx) => (
                                <div key={idx} className="flex items-start gap-2 px-2 py-1.5 bg-slate-800/30 rounded border border-slate-700/20 mb-1">
                                  <CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5" />
                                  <div>
                                    <span className="text-[10px] text-slate-300">{int.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                    {int.notes && <div className="text-[9px] text-slate-500">{int.notes}</div>}
                                    <div className="text-[9px] text-slate-600">{formatDate(int.performedAt)} • {int.performedBy}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredDeals.length === 0 && (
            <div className="text-center py-16">
              <Activity className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500 text-sm">No deals match the selected filter.</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ INTERVENTION MODAL ═══ */}
      {interventionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setInterventionModal(null)}>
          <div className="bg-[#111827] border border-slate-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-1">
              {interventionModal.type === 'call_client' && '📞 Log Client Call'}
              {interventionModal.type === 'send_custom_email' && '✉️ Send Custom Email'}
              {interventionModal.type === 'escalate' && '⬆️ Escalate Deal'}
            </h3>
            <p className="text-slate-400 text-xs mb-4">
              {interventionModal.type === 'call_client' && 'Record notes from your call with the client.'}
              {interventionModal.type === 'send_custom_email' && 'Add context for the custom email being sent.'}
              {interventionModal.type === 'escalate' && 'Provide reason for escalation to management.'}
            </p>
            <textarea
              value={interventionNotes}
              onChange={e => setInterventionNotes(e.target.value)}
              placeholder={
                interventionModal.type === 'call_client'
                  ? 'Call outcome, next steps, client feedback...'
                  : interventionModal.type === 'send_custom_email'
                  ? 'Email subject/context...'
                  : 'Reason for escalation...'
              }
              className="w-full h-24 px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-orange-500 resize-none"
            />
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={confirmIntervention}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 rounded-lg text-white text-sm font-medium hover:bg-orange-700 transition-all"
              >
                <CheckCircle2 className="w-4 h-4" /> Confirm
              </button>
              <button
                onClick={() => setInterventionModal(null)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-700 rounded-lg text-slate-300 text-sm font-medium hover:bg-slate-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ NOTIFICATION TOAST ═══ */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className="bg-green-900/90 border border-green-500/40 rounded-xl px-5 py-3 text-green-300 text-sm font-medium shadow-2xl backdrop-blur-sm">
            {notification}
          </div>
        </div>
      )}
    </Layout>
  );
};
